import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ExclamationCircleIcon, ClockIcon, SparklesIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

export default function TrialBanner() {
  const { user } = useAuth();
  const [starterLoading, setStarterLoading] = useState(false);
  const [starterError, setStarterError] = useState('');

  async function handleStarterCheckout() {
    setStarterLoading(true);
    setStarterError('');
    try {
      const res = await api.post('/billing/create-starter-checkout-session');
      window.location.href = res.data.url;
    } catch (err) {
      setStarterError(err.response?.data?.error || 'Unable to start checkout. Please try again.');
      setStarterLoading(false);
    }
  }

  const info = useMemo(() => {
    if (!user) return null;

    const status = user.subscriptionStatus;

    // Active subscribers (full or starter) — show nothing
    if (status === 'active') return null;

    if (status === 'trial' && !user.trialEndsAt) return null;

    if (status === 'trial' && user.trialEndsAt) {
      const now = new Date();
      const endsAt = new Date(user.trialEndsAt);
      const daysLeft = Math.max(0, Math.ceil((endsAt - now) / (1000 * 60 * 60 * 24)));

      if (daysLeft <= 0) {
        return { type: 'expired', daysLeft: 0 };
      }
      if (daysLeft <= 7) {
        return { type: 'warning', daysLeft };
      }
      if (daysLeft <= 14) {
        return { type: 'info', daysLeft };
      }
      // More than 14 days — show a subtle banner
      return { type: 'info', daysLeft };
    }

    if (status === 'past_due') {
      return { type: 'past_due', daysLeft: 0 };
    }

    if (status === 'cancelled' || status === 'expired') {
      return { type: 'expired', daysLeft: 0 };
    }

    return null;
  }, [user]);

  if (user?.subscriptionStatus === 'starter') {
    return (
      <div className="mb-6 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 text-xs font-semibold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full uppercase tracking-wide">Starter</span>
          <p className="text-sm text-slate-600">
            2 uploads/month · view history · plain-language results
            <span className="hidden sm:inline text-slate-400"> · AI features (Trends, Prep, etc.) require Full Access</span>
          </p>
        </div>
        <Link
          to="/pricing"
          className="text-sm bg-sky-600 text-white px-4 py-1.5 rounded-lg font-medium hover:bg-sky-700 transition-colors whitespace-nowrap flex-shrink-0"
        >
          Upgrade — $25/mo
        </Link>
      </div>
    );
  }

  if (!info) return null;

  if (info.type === 'expired') {
    return (
      <div className="mb-6 bg-slate-50 border border-slate-200 rounded-xl p-4">
        <div className="flex items-start gap-3 mb-4">
          <ExclamationCircleIcon className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-slate-800">Your free trial has ended</p>
            <p className="text-sm text-slate-500 mt-0.5">Choose a plan to keep using HealthClear AI.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {/* $5 Starter option */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Starter</span>
              <span className="text-lg font-bold text-slate-800">$5<span className="text-sm font-normal text-slate-400">/mo</span></span>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Not ready for the $25 plan? Pay $5/month to <strong className="text-slate-800">keep your progress</strong> and still:
            </p>
            <ul className="text-sm text-slate-600 space-y-1 mb-4">
              <li className="flex items-center gap-1.5"><span className="text-green-500">✓</span> View all your past results & history</li>
              <li className="flex items-center gap-1.5"><span className="text-green-500">✓</span> Upload 2 new reports per month</li>
              <li className="flex items-center gap-1.5"><span className="text-green-500">✓</span> Plain-language explanations</li>
              <li className="flex items-center gap-1.5"><span className="text-green-500">✓</span> See what's flagged or in range</li>
            </ul>
            {starterError && <p className="text-xs text-red-600 mb-2">{starterError}</p>}
            <button
              onClick={handleStarterCheckout}
              disabled={starterLoading}
              className="w-full text-sm bg-slate-800 text-white py-2 rounded-lg font-medium hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {starterLoading ? 'Redirecting…' : 'Keep my progress — $5/mo'}
            </button>
          </div>

          {/* $25 Full option */}
          <div className="bg-sky-600 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-1 text-xs font-semibold text-blue-100 uppercase tracking-wide">
                <SparklesIcon className="w-3.5 h-3.5" /> Full Access
              </span>
              <span className="text-lg font-bold">$25<span className="text-sm font-normal text-blue-200">/mo</span></span>
            </div>
            <p className="text-sm text-blue-100 mb-3">Everything in Starter, plus:</p>
            <ul className="text-sm text-blue-100 space-y-1 mb-4">
              <li className="flex items-center gap-1.5"><span className="text-white">✓</span> Unlimited uploads</li>
              <li className="flex items-center gap-1.5"><span className="text-white">✓</span> Trend tracking across visits</li>
              <li className="flex items-center gap-1.5"><span className="text-white">✓</span> Appointment prep brief</li>
              <li className="flex items-center gap-1.5"><span className="text-white">✓</span> Medication interaction check</li>
              <li className="flex items-center gap-1.5"><span className="text-white">✓</span> Preventive care gaps</li>
            </ul>
            <Link
              to="/pricing"
              className="block text-center text-sm bg-white text-sky-600 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Get full access — $25/mo
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (info.type === 'past_due') {
    return (
      <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <ExclamationCircleIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700 font-medium">
            Your payment is past due. Please update your billing details to keep access.
          </p>
        </div>
        <Link
          to="/pricing"
          className="text-sm bg-amber-600 text-white px-4 py-1.5 rounded-lg font-medium hover:bg-amber-700 transition-colors whitespace-nowrap flex-shrink-0"
        >
          Update billing →
        </Link>
      </div>
    );
  }

  // Trial with days remaining
  const isUrgent = info.daysLeft <= 7;
  const bgClass = isUrgent ? 'bg-amber-50 border-amber-200' : 'bg-sky-50 border-sky-200';
  const iconClass = isUrgent ? 'text-amber-500' : 'text-sky-500';
  const textClass = isUrgent ? 'text-amber-700' : 'text-sky-700';
  const btnClass = isUrgent
    ? 'bg-amber-600 hover:bg-amber-700 text-white'
    : 'bg-sky-600 hover:bg-sky-700 text-white';

  return (
    <div className={`mb-6 border rounded-xl px-4 py-3 flex items-center justify-between gap-4 ${bgClass}`}>
      <div className="flex items-center gap-2">
        <ClockIcon className={`w-5 h-5 flex-shrink-0 ${iconClass}`} />
        <p className={`text-sm font-medium ${textClass}`}>
          {info.daysLeft === 1
            ? 'Your free trial ends tomorrow.'
            : `${info.daysLeft} days left in your free trial.`}
          {isUrgent && ' Subscribe to keep full access.'}
        </p>
      </div>
      <Link
        to="/pricing"
        className={`text-sm px-4 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap flex-shrink-0 ${btnClass}`}
      >
        View plans →
      </Link>
    </div>
  );
}
